import { useState } from "react";
import { useNavigate } from 'react-router-dom';
import loginIllustration from "../assets/images/login-illustration.svg";

function CreateAccountForm() {
  const navigate = useNavigate();
  const [successMessage, setSuccessMessage] = useState("");
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    cnpj: "",
    companyName: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    cnpj: "",
    companyName: "",
    password: "",
    confirmPassword: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    const updatedValue =
      name === "cnpj" ? formatCNPJ(value) : value;

    setFormData({
      ...formData,
      [name]: updatedValue,
    });

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const formatCNPJ = (value) => {
    return value
      .replace(/\D/g, "")
      .replace(/^(\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2")
      .slice(0, 18);
  };

  const validateCNPJ = (cnpj) => {
    cnpj = cnpj.replace(/[^\d]+/g, '');

    if (cnpj.length !== 14) return false;
    if (/^(\d)\1{13}$/.test(cnpj)) return false;

    let tamanho = cnpj.length - 2;
    let numeros = cnpj.substring(0, tamanho);
    let digitos = cnpj.substring(tamanho);
    let soma = 0;
    let pos = tamanho - 7;

    for (let i = tamanho; i >= 1; i--) {
      soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
      if (pos < 2) pos = 9;
    }

    let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== parseInt(digitos.charAt(0))) return false;

    tamanho += 1;
    numeros = cnpj.substring(0, tamanho);
    soma = 0;
    pos = tamanho - 7;
    for (let i = tamanho; i >= 1; i--) {
      soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
      if (pos < 2) pos = 9;
    }

    resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    return resultado === parseInt(digitos.charAt(1));
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = {
      name: "",
      email: "",
      cnpj: "",
      companyName: "",
      password: "",
      confirmPassword: "",
    };

    if (!formData.name || formData.name.trim().length < 2) {
      newErrors.name = "O nome é obrigatório";
      valid = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = "O e-mail é obrigatório";
      valid = false;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Formato de e-mail inválido";
      valid = false;
    }

    if (!formData.cnpj) {
      newErrors.cnpj = "O CNPJ é obrigatório";
      valid = false;
    } else if (!validateCNPJ(formData.cnpj)) {
      newErrors.cnpj = "CNPJ inválido";
      valid = false;
    }

    if (!formData.companyName || formData.companyName.trim().length < 2) {
      newErrors.companyName = "O nome da empresa é obrigatório";
      valid = false;
    }

    if (!formData.password.trim()) {
      newErrors.password = "A senha é obrigatória";
      valid = false;
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "As senhas não coincidem";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    const cleanCNPJ = formData.cnpj.replace(/\D/g, "");

    const body = {
      companyName: formData.companyName,
      cnpj: Number(cleanCNPJ),
      name: formData.name,
      email: formData.email,
      password: formData.password
    };

    try {
      const response = await fetch(
        "https://ad-insights-api-0f2fcea0deda.herokuapp.com/api/agency/register",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setSuccessMessage("Agência cadastrada com sucesso!");
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setServerError(result.msg || "Erro ao cadastrar agência.");
        setSuccessMessage("");
      }
    } catch (error) {
      setServerError("Erro inesperado. Tente novamente mais tarde.");
      setSuccessMessage("");
    }
    finally {
      setLoading(false);
    }
  };

  const isFormValid =
    formData.name &&
    formData.email &&
    formData.cnpj &&
    formData.password &&
    formData.confirmPassword &&
    !errors.name &&
    !errors.email &&
    !errors.cnpj &&
    !errors.password &&
    !errors.confirmPassword;

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="image-section">
          <div className="image-content">
            <img
              src={loginIllustration}
              alt="Login illustration"
              className="login-image"
            />
            <div className="carousel-indicators">
              <svg
                width="89"
                height="16"
                viewBox="0 0 89 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="8.5" cy="8" r="8" fill="#4285F3"></circle>
                <circle cx="32.5" cy="8" r="8" fill="#C6DCFF"></circle>
                <circle cx="56.5" cy="8" r="8" fill="#C6DCFF"></circle>
                <circle cx="80.5" cy="8" r="8" fill="#C6DCFF"></circle>
              </svg>
            </div>
          </div>
        </div>
        <div className="form-section">
          <div className="form-container">
            <form className="login-form-wrapper" onSubmit={handleSubmit}>
              <div className="form-header">
                <div className="form-title">Cadastre-se</div>
                <div className="form-subtitle">
                  Insira seus dados para continuar
                </div>
              </div>

              <div className="form-content">
                <div className="form-fields">
                  <div className="form-field">
                    <div className="field-label">Nome</div>
                    <div className={`input-container ${errors.name ? "input-error" : ""}`}>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Digite seu nome completo"
                        className="form-input"
                      />
                    </div>
                    {errors.name && <div className="error-message">{errors.name}</div>}
                  </div>
                  <div className="form-field">
                    <div className="field-label">E-mail</div>
                    <div className={`input-container ${errors.email ? "input-error" : ""}`}>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Digite seu melhor e-mail"
                        className="form-input"
                      />
                    </div>
                    {errors.email && <div className="error-message">{errors.email}</div>}
                  </div>
                  <div className="form-field">
                    <div className="field-label">Nome da empresa</div>
                    <div className={`input-container ${errors.companyName ? "input-error" : ""}`}>
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        placeholder="Digite o nome da empresa"
                        className="form-input"
                      />
                    </div>
                    {errors.companyName && <div className="error-message">{errors.companyName}</div>}
                  </div>
                  <div className="form-field">
                    <div className="field-label">CNPJ</div>
                    <div className={`input-container ${errors.cnpj ? "input-error" : ""}`}>
                      <input
                        type="text"
                        name="cnpj"
                        value={formData.cnpj}
                        onChange={handleChange}
                        placeholder="Digite seu CNPJ"
                        className="form-input"
                      />
                    </div>
                    {errors.cnpj && <div className="error-message">{errors.cnpj}</div>}
                  </div>
                  <div className="form-field">
                    <div className="field-header">
                      <div className="field-label">Senha:</div>
                    </div>
                    <div className="form-fields">
                      <div className="form-field">
                        <div className={`input-container ${errors.password ? "input-error" : ""}`}>
                          <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Digite sua senha"
                          />
                          <div className="password-toggle" onClick={togglePasswordVisibility}>
                            {showPassword ? (
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clip-path="url(#clip0_886_2866)">
                                  <path d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" stroke="#4285F3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                  <path d="M2 12C3.60014 7.90264 7.33603 5 12 5C16.664 5 20.3999 7.90264 22 12C20.3999 16.0974 16.664 19 12 19C7.33603 19 3.60014 16.0974 2 12Z" stroke="#4285F3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                </g>
                                <defs>
                                  <clipPath id="clip0_886_2866">
                                    <rect width="24" height="24" fill="white" />
                                  </clipPath>
                                </defs>
                              </svg>
                            ) : (
                              <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clip-path="url(#clip0_886_5106)">
                                  <path d="M11.2302 5.07319C11.6449 5.02485 12.0685 5 12.5 5C17.164 5 20.8999 7.90264 22.5 12C22.1054 13.0104 21.5809 13.9482 20.9447 14.7877M7.01962 6.51944C4.97955 7.76406 3.40111 9.69259 2.5 12C4.10014 16.0974 7.83603 19 12.5 19C14.5376 19 16.398 18.446 17.9806 17.4804M10.3788 9.87859C9.83582 10.4215 9.5 11.1715 9.5 12C9.5 13.6569 10.8431 15 12.5 15C13.3285 15 14.0785 14.6642 14.6214 14.1212" stroke="#4285F3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                  <path d="M4.5 4L20.5 20" stroke="#4285F3" stroke-width="2" stroke-linecap="round" />
                                </g>
                                <defs>
                                  <clipPath id="clip0_886_5106">
                                    <rect width="24" height="24" fill="white" transform="translate(0.5)" />
                                  </clipPath>
                                </defs>
                              </svg>
                            )}
                          </div>
                        </div>
                        {errors.password && (
                          <div className="error-message">{errors.password}</div>
                        )}
                      </div>
                      <div className="form-field">
                        <div className="field-header">
                          <div className="field-label">Confirmar Senha</div>
                        </div>
                        <div className={`input-container ${errors.confirmPassword ? "input-error" : ""}`}>
                          <input
                            type={showPassword ? "text" : "password"}
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Confirme sua senha"
                          />
                          <div className="password-toggle" onClick={togglePasswordVisibility}>
                            {showPassword ? (
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clip-path="url(#clip0_886_2866)">
                                  <path d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" stroke="#4285F3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                  <path d="M2 12C3.60014 7.90264 7.33603 5 12 5C16.664 5 20.3999 7.90264 22 12C20.3999 16.0974 16.664 19 12 19C7.33603 19 3.60014 16.0974 2 12Z" stroke="#4285F3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                </g>
                                <defs>
                                  <clipPath id="clip0_886_2866">
                                    <rect width="24" height="24" fill="white" />
                                  </clipPath>
                                </defs>
                              </svg>
                            ) : (
                              <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clip-path="url(#clip0_886_5106)">
                                  <path d="M11.2302 5.07319C11.6449 5.02485 12.0685 5 12.5 5C17.164 5 20.8999 7.90264 22.5 12C22.1054 13.0104 21.5809 13.9482 20.9447 14.7877M7.01962 6.51944C4.97955 7.76406 3.40111 9.69259 2.5 12C4.10014 16.0974 7.83603 19 12.5 19C14.5376 19 16.398 18.446 17.9806 17.4804M10.3788 9.87859C9.83582 10.4215 9.5 11.1715 9.5 12C9.5 13.6569 10.8431 15 12.5 15C13.3285 15 14.0785 14.6642 14.6214 14.1212" stroke="#4285F3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                  <path d="M4.5 4L20.5 20" stroke="#4285F3" stroke-width="2" stroke-linecap="round" />
                                </g>
                                <defs>
                                  <clipPath id="clip0_886_5106">
                                    <rect width="24" height="24" fill="white" transform="translate(0.5)" />
                                  </clipPath>
                                </defs>
                              </svg>
                            )}
                          </div>
                        </div>
                        {errors.confirmPassword && (
                          <div className="error-message">{errors.confirmPassword}</div>
                        )}
                      </div>
                      {successMessage && (
                        <div className="success-message">
                          {successMessage}
                        </div>
                      )}
                    </div>
                    {errors.password && (
                      <div className="error-message">{errors.password}</div>
                    )}
                  </div>
                </div>

                {serverError && <div className="error-message">{serverError}</div>}

                <div className="form-actions">
                  <button
                    type="submit"
                    className={`login-button ${!isFormValid || loading ? "button-disabled" : ""}`}
                    disabled={!isFormValid || loading}
                  >
                    {loading ? "Enviando..." : "Cadastrar"}
                  </button>
                  <div className="signup-section">
                    <div className="signup-text">Já possui conta?</div>
                    <button
                      type="button"
                      className="signup-link"
                      onClick={() => navigate("/login")}
                    >
                      Voltar para o login
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateAccountForm;
